import { Alert, AppBar, Box, Button, CircularProgress, Divider, Paper, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../services/HttpService';
import { Member, newMember, CompanyAddress, newCompanyAddress, newAddress, Address, CompanyContact, newCompanyContact } from '../../../../model';
import { TabPanel, TabProps } from '../../../../components/ui/TabPanel';
import { DataInfoTable, DataListInfoTable } from '../../../../components/tables/DataTable';
import { Accessgroup } from '../../../../model/System';
import { dayjsService } from '../../../../services/dayjsService';
import { CenterSpinner } from '../../../../components/ui/CenterSpinner';
import { ManyToManyForm, ManyToManyFormRef } from '../ManyToManyForm';
import { AddressForm, AddressFormRef } from '../../../../model/forms/AddressForm';
import { MemberForm, MemberFormRef } from '../../../../model/forms/MemberForm';
import { SelectServerSearch } from '../../../../components/ui/DropdownSelect';
import { newUserAccessgroup, newUserContact, UserContact } from '../../../../model/User';

interface ContactOverviewProps {
  item: Member;
  getError?: string;
  onClose?: () => void;
  onItemChange?: (item: Member) => void;
}

export const ContactOverview = (props: ContactOverviewProps) => {
  const theme = useTheme();
  const { getError, onItemChange } = props;
  const { api, error, isLoading } = useApi();
  const { api: modApi, error: modError, isLoading: modIsLoading } = useApi();
  const [item, setItem] = useState<Member>(props.item);
  const [displayData, setDisplayData] = useState<Record<string, string>>(null);
  const [companyContact, setCompanyContact] = useState<CompanyContact>(newCompanyContact);
  const [userContact, setUserContact] = useState<UserContact>(newUserContact);
  const [tabIndex, setTabIndex] = useState(0);
  const [accessgroupIds, setAccessgroupIds] = useState<number[]>([]);

  const addMemberRef = useRef<MemberFormRef>(null);
  const addCompanyContactRef = useRef<ManyToManyFormRef>(null);
  const addUserContactRef = useRef<ManyToManyFormRef>(null);
  const addAddressRef = useRef<AddressFormRef>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!props.item || props.item.id === 0) {
      setTabIndex(1);
    } else {
      getItem();
      setDisplayData({
        "ID": String(props?.item?.id),
        "Member number": props?.item?.member_number,
        "First name": props?.item?.first_name,
        "Last name": props?.item?.last_name,
        "Name": props?.item?.name,
      });
    }
    
    setAccessgroupIds([]);
  }, [props.item]);

  useEffect(() => {
    setDisplayData((prev) => ({
      ...prev,
      "Accounting number": item?.accounting_number,
      "Employee": item?.employee ? "Yes" : "no",
      "DOB": dayjsService.toDateDisplayString(item?.dob),
      "SSN": item?.ssn,
      "First Membership Date": dayjsService.toDateDisplayString(item?.first_membership_date),
      "Membership From Date": dayjsService.toDateDisplayString(item?.membership_from_date),
      "Membership To Date": dayjsService.toDateDisplayString(item?.membership_to_date),
      "App Activated At": dayjsService.toDateDisplayString(item?.app_activated_at),
      "Web Activated At": dayjsService.toDateDisplayString(item?.web_activated_at),
      "Sex ID": String(item?.sex_id),
    }));
  }, [item]);

  const getItem = () => {
    api("/getContact", "POST", { id: props.item.id, with_relations: true }).then((res) => {
      if (res.statusCode === 200) {
        const item: Member = res.contact;
        setItem(item);
      }
    });
  }

  const reset = () => {
    setItem(() => ({ ...item }));
  }

  const changeTab = (index: number, performChange: boolean = true): boolean => {
    if (!item.id) {
      if (performChange) {
        setTabIndex(1);
      }

      return index === 1;
    }

    if (performChange) {
      setTabIndex(index);
    }

    return true;
  }

  const saveItem = () => {
    const itemRef = addMemberRef.current.getItem();

    // when creating contact, accessgroups can be added, this will also create a user, user_contact and user_accessgroups
    if (item.id === 0 && accessgroupIds.length > 0) {
      itemRef.user_accessgroups = [];
      accessgroupIds.forEach(id => {
        itemRef.user_accessgroups.push(newUserAccessgroup(0, id))
      })
    }

    console.log("submitting", itemRef, "valid", addMemberRef.current.valid());

    if (addMemberRef.current.valid()) {
      api("/manage/member/postMember", "POST", { ...itemRef }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          onItemChange(res.member);
          setTabIndex(0);
        }
      });
    }
  }

  const saveUserContact = () => {
    const newRel = addUserContactRef.current.getItem();
    console.log("item", newRel, "valid", addUserContactRef.current.valid());

    if (addUserContactRef.current.valid()) {
      const isNew = userContact.id === 0;

      const newRelItem: UserContact = {
        ...userContact,
        contact_id: item.id,
        oidc_user_id: newRel.select_id,
      };

      api("/manage/user/postUserContact", "POST", newRelItem).then(res => {
        if (res?.statusCode === 200) {
          setUserContact(res.user_contact);

          setItem((prev) => ({
            ...prev,
            user_contacts: isNew
              ? [...(prev.user_contacts || []), res.user_contact]
              : prev.user_contacts.map(u =>
                u.id === newRelItem.id ? res.user_contact : u
              ),
          }));
        }
      });
    }
  }

  const saveCompanyContact = () => {
    const newRel = addCompanyContactRef.current.getItem();
    console.log("item", newRel, "valid", addCompanyContactRef.current.valid());

    if (addCompanyContactRef.current.valid()) {
      const isNew = companyContact.id === 0;

      const newRelItem: CompanyContact = {
        ...companyContact,
        contact_id: item.id,
        company_id: newRel.select_id,
      };

      api("/manage/company/postCompanyContact", "POST", newRelItem).then(res => {
        if (res?.statusCode === 200) {
          setCompanyContact(res.company_contact);

          setItem((prev) => ({
            ...prev,
            company_contacts: isNew
              ? [...(prev.company_contacts || []), res.company_contact]
              : prev.company_contacts.map(u =>
                u.id === newRelItem.id ? res.company_contact : u
              ),
          }));
        }
      });
    }
  }

  const saveCompanyAddress = () => {
    // const newRel = addAddressRef.current.getItem();
    // console.log("item", newRel, "valid", addAddressRef.current.valid());

    // if (addAddressRef.current.valid()) {
    //   const isNew = companyAddress.id === 0;

    //   const newRelItem: CompanyAddress = {
    //     ...companyAddress,
    //     id: companyAddress.id,
    //     address_id: newRel.id,
    //     company_id: item.id,
    //     valid_from: newRel.valid_from,
    //     valid_to: newRel.valid_to,
    //     primary_address: newRel.primary,
    //     addressline1: newRel.addressline1,
    //     addressline2: newRel.addressline2,
    //     addressline3: newRel.addressline3,
    //     postcode: newRel.postcode,
    //     postoffice: newRel.postoffice,
    //   };

    //   api("/manage/company/postCompanyAddress", "POST", newRelItem).then(res => {
    //     if (res?.statusCode === 200) {
    //       setCompanyAddress(res.company_address);

    //       setItem((prev) => ({
    //         ...prev,
    //         company_addresses: isNew
    //           ? [...(prev.company_addresses || []), res.company_address]
    //           : prev.company_addresses.map(u =>
    //             u.id === newRelItem.id ? res.company_address : u
    //           ),
    //       }));
    //     }
    //   });
    // }
  }

  return (
    <>
      <AppBar position="static"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? "inherit" : theme.palette.grey[300],
          color: theme.palette.mode === 'dark' ? "white" : "black"
        }}
      >
        {error && <Alert severity='error'>{error}</Alert>}

        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant={"scrollable"}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="manage company tabs"
        >
          {[
            "Overview",
            item.id ? "Edit" : "Create",
            "User",
            "Companies",
            // "Addresses",
          ].map((name, index) => (
            <Tab disabled={!changeTab(index, false)} key={index} label={name} sx={{ fontWeight: "bold", textTransform: "none" }} {...TabProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      <Box sx={{ mt: 2, p: { xs: 0, md: 3 }, minHeight: 400 }}>
        <TabPanel value={tabIndex} index={0} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <DataInfoTable data={displayData} onEdit={() => changeTab(1)} />
            <CenterSpinner loading={isLoading}>
              <>
                <DataListInfoTable title={"Users"}
                  data={item.user_contacts ?? []}
                  displayField="oidc_user_name"
                  onAdd={() => { setUserContact(newUserContact()); changeTab(2) }}
                  onEdit={(id) => { setUserContact(item.user_contacts.find(u => u.id === id)); changeTab(2) }}
                  onDelete={(id) => alert("delete not implemented")}
                />
                <DataListInfoTable title={"Companies"}
                  data={item.company_contacts ?? []}
                  displayField="company_name"
                  onAdd={() => { setCompanyContact(newCompanyContact()); changeTab(3) }}
                  onEdit={(id) => { setCompanyContact(item.company_contacts.find(u => u.id === id)); changeTab(2) }}
                  onDelete={(id) => alert("delete not implemented")}
                  onPrimaryCahnge={(id) => { console.log("setting primary to", id); alert("primary change not implemented") }}
                  primaryField='main_contact'
                />
                {/* <DataListInfoTable title={"Adresser"}
                  data={item.company_addresses ?? []}
                  displayField="addressline1"
                  onAdd={() => { setCompanyAddress(newCompanyAddress()); changeTab(3) }}
                  onEdit={(id) => { setCompanyAddress(item.company_addresses.find(u => u.id === id)); changeTab(3) }}
                  onDelete={(id) => alert("delete not implemented")}
                  onPrimaryCahnge={(id) => console.log("setting primary to", id)}
                  primaryField="primary_address"
                /> */}
              </>
            </CenterSpinner>
            {props.onClose && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', p: 2 }}>
                <Button variant='contained' color='darkgrey' onClick={props.onClose}>Lukk</Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabIndex} index={1} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <MemberForm item={item} error={getError} ref={addMemberRef} />
            {item.id === 0 && (
              <Box>
                <Alert sx={{marginBottom: 0.5}} severity='info'>By adding accessgroups, a user will automatically be created with them.</Alert>
                <SelectServerSearch
                  label="Accessgroups"
                  tableName="accessgroup"
                  selected={accessgroupIds}
                  singleSelect={false}
                  onChange={setAccessgroupIds}
                />
              </Box>
            )}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button variant='contained' color='darkgrey' onClick={reset}>Tilbakestill</Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant='contained'
                  color='darkgrey'
                  onClick={() => {
                    if (changeTab(0, false)) {
                      changeTab(0);
                    } else if (props.onClose) {
                      props.onClose();
                    }
                  }}
                >
                  Avbryt
                </Button>
                <Button
                  variant='contained'
                  color='info'
                  onClick={saveItem}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={22} />}
                >
                  {item.id ? "Save" : "Create"}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabIndex} index={2} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: userContact.oidc_user_id,
              }}
              selectItemName="Users"
              selectTable="oidc_user"
              ref={addUserContactRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setUserContact(newUserContact()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveUserContact}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {userContact.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Users"}
              data={item.user_contacts ?? []}
              displayField="oidc_user_name"
              onAdd={() => { setUserContact(newUserContact()) }}
              onEdit={(id) => { setUserContact(item.user_contacts.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>

        <TabPanel value={tabIndex} index={3} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: companyContact.contact_id,
              }}
              selectItemName="Companies"
              selectTable="company"
              ref={addCompanyContactRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setCompanyContact(newCompanyContact()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveCompanyContact}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {companyContact.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Companies"}
              data={item.company_contacts ?? []}
              displayField="company_name"
              onAdd={() => { setCompanyContact(newCompanyContact()) }}
              onEdit={(id) => { setCompanyContact(item.company_contacts.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>

        {/* <TabPanel value={tabIndex} index={3} sx={{ minHeight: 200 }}>
          <>
            <AddressForm
              item={{
                id: companyAddress.address_id,
                name: "",
                refrence_id: companyAddress.company_id,
                valid_from: companyAddress.valid_from,
                valid_to: companyAddress.valid_to,
                primary: companyAddress.primary_address,
                addressline1: companyAddress.addressline1,
                addressline2: companyAddress.addressline2,
                addressline3: companyAddress.addressline3,
                postcode: companyAddress.postcode,
                postoffice: companyAddress.postoffice,
              }}
              checkPrimary
              fromAndToDate
              ref={addAddressRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setCompanyAddress(newCompanyAddress()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveCompanyAddress}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {companyAddress.address_id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Adresser"}
              data={item.company_addresses ?? []}
              displayField="addressline1"
              onAdd={() => setCompanyAddress(newCompanyAddress())}
              onEdit={(id) => setCompanyAddress(item.company_addresses.find(u => u.id === id))}
            />
          </>
        </TabPanel> */}
      </Box>
    </>
  )
}